import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../components/Icon';
import { Button, Input, Select, Modal, Toast } from '../components/UI';
import { problemsAPI, youthAPI } from '../services/api';

function getUser() {
  try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
}

const CATEGORIES = [
  { value: 'ishsizlik', label: 'Ishsizlik', color: '#ef4444', bg: '#fef2f2', icon: 'briefcase' },
  { value: 'talim', label: "Ta'lim", color: '#3b82f6', bg: '#eff6ff', icon: 'graduation' },
  { value: 'uy_joy', label: 'Uy-joy', color: '#f59e0b', bg: '#fffbeb', icon: 'home' },
  { value: 'sogliq', label: "Sog'liq", color: '#10b981', bg: '#ecfdf5', icon: 'heart' },
  { value: 'moliyaviy', label: 'Moliyaviy', color: '#8b5cf6', bg: '#f5f3ff', icon: 'trend' },
  { value: 'huquqiy', label: 'Huquqiy', color: '#6366f1', bg: '#eef2ff', icon: 'shield' },
  { value: 'oilaviy', label: 'Oilaviy', color: '#ec4899', bg: '#fdf2f8', icon: 'heart' },
  { value: 'ijtimoiy', label: 'Ijtimoiy himoya', color: '#14b8a6', bg: '#f0fdfa', icon: 'users' },
  { value: 'boshqa', label: 'Boshqa', color: '#64748b', bg: '#f8fafc', icon: 'flag' },
];

const PRIORITIES = [
  { value: 'shoshilinch', label: 'Shoshilinch', color: '#dc2626', bg: '#fef2f2', dot: '#ef4444' },
  { value: 'yuqori', label: 'Yuqori', color: '#ea580c', bg: '#fff7ed', dot: '#f97316' },
  { value: "o'rta", label: "O'rta", color: '#ca8a04', bg: '#fefce8', dot: '#eab308' },
  { value: 'past', label: 'Past', color: '#16a34a', bg: '#f0fdf4', dot: '#22c55e' },
];

const STATUSES = [
  { value: '', label: 'Barchasi', icon: 'clipboardList' },
  { value: 'yangi', label: 'Yangi', icon: 'zap', color: '#6366f1', bg: '#eef2ff' },
  { value: 'jarayonda', label: 'Jarayonda', icon: 'clock', color: '#f59e0b', bg: '#fffbeb' },
  { value: 'hal_qilindi', label: 'Hal qilindi', icon: 'checkCircle', color: '#22c55e', bg: '#f0fdf4' },
  { value: 'yopildi', label: 'Yopildi', icon: 'lock', color: '#64748b', bg: '#f8fafc' },
];

const getCat = (v) => CATEGORIES.find(c => c.value === v) || CATEGORIES[8];
const getPri = (v) => PRIORITIES.find(p => p.value === v) || PRIORITIES[2];
const getSt = (v) => STATUSES.find(s => s.value === v) || STATUSES[0];

// ===== MAIN PAGE =====
export default function ProblemsPage() {
  const user = getUser();
  const isAdmin = user?.role === 'viloyat_admin' || user?.role === 'tuman_admin';

  const [problems, setProblems] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const [statusF, setStatusF] = useState('');
  const [categoryF, setCategoryF] = useState('');
  const [priorityF, setPriorityF] = useState('');
  const [search, setSearch] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [respondItem, setRespondItem] = useState(null);

  const showToast = (msg, type = 'success') => setToast({ msg, type });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusF) params.status = statusF;
      if (categoryF) params.category = categoryF;
      if (priorityF) params.priority = priorityF;
      if (search) params.search = search;
      const [pRes, sRes] = await Promise.all([
        problemsAPI.list(params),
        problemsAPI.stats(),
      ]);
      setProblems(pRes.data);
      setStats(sRes.data);
    } catch { showToast("Ma'lumotlar yuklanmadi", 'error'); }
    setLoading(false);
  }, [statusF, categoryF, priorityF, search]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (data) => {
    try {
      await problemsAPI.create(data);
      showToast("Muammo qo'shildi");
      setFormOpen(false);
      load();
    } catch { showToast("Xatolik yuz berdi", 'error'); }
  };

  const handleUpdate = async (data) => {
    try {
      await problemsAPI.update(editItem.id, data);
      showToast("Muammo yangilandi");
      setEditItem(null);
      load();
    } catch { showToast("Xatolik yuz berdi", 'error'); }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await problemsAPI.patch(id, { status: newStatus });
      showToast("Holat yangilandi");
      load();
    } catch { showToast("Xatolik", 'error'); }
  };

  const handleDelete = async () => {
    try {
      await problemsAPI.delete(deleteId);
      showToast("Muammo o'chirildi");
      setDeleteId(null);
      load();
    } catch { showToast("Xatolik", 'error'); }
  };

  const handleRespond = async (data) => {
    try {
      await problemsAPI.respond(respondItem.id, data);
      showToast("Javob yuborildi");
      setRespondItem(null);
      setViewItem(null);
      load();
    } catch { showToast("Xatolik", 'error'); }
  };

  async function loadAndView(id) {
    try { const r = await problemsAPI.get(id); setViewItem(r.data); } catch {}
  }
  async function loadAndEdit(id) {
    try { const r = await problemsAPI.get(id); setEditItem(r.data); } catch {}
  }

  return (
    <div>
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      {/* HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: '#1e293b', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="alertTriangle" size={24} color="#6366f1" /> Muammolar
          </h2>
          <p style={{ fontSize: 13, color: '#94a3b8', margin: '4px 0 0' }}>
            {isAdmin ? 'Yoshlar muammolari va hokimlik javobi' : "Yoshlar muammolarini ro'yxatga olish"}
          </p>
        </div>
      </div>

      {/* STAT CARDS */}
      <div className="prob-stats" style={{ display: 'grid', gap: 14, marginBottom: 24 }}>
        <StatCard icon="clipboardList" label="Jami" value={stats.total || 0} color="#6366f1" />
        <StatCard icon="zap" label="Yangi" value={stats.yangi || 0} color="#818cf8" />
        <StatCard icon="clock" label="Jarayonda" value={stats.jarayonda || 0} color="#f59e0b" />
        <StatCard icon="checkCircle" label="Hal qilindi" value={stats.hal_qilindi || 0} color="#22c55e" />
        <StatCard icon="alertTriangle" label="Muddati o'tgan" value={stats.overdue || 0} color="#ef4444" />
        <StatCard icon="flag" label="Shoshilinch" value={stats.shoshilinch || 0} color="#dc2626" />
      </div>

      {/* STATUS TABS - faqat MFY */}
      {!isAdmin && <div style={{ display: 'flex', gap: 6, marginBottom: 16, overflowX: 'auto', paddingBottom: 4 }}>
        {STATUSES.map(s => {
          const active = statusF === s.value;
          const count = s.value === '' ? (stats.total || 0)
            : s.value === 'yangi' ? (stats.yangi || 0)
            : s.value === 'jarayonda' ? (stats.jarayonda || 0)
            : s.value === 'hal_qilindi' ? (stats.hal_qilindi || 0)
            : (stats.yopildi || 0);
          return (
            <button key={s.value} onClick={() => setStatusF(s.value)} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px', borderRadius: 12,
              border: active ? '2px solid ' + (s.color || '#6366f1') : '2px solid #e2e8f0',
              background: active ? (s.bg || '#eef2ff') : '#fff',
              color: active ? (s.color || '#6366f1') : '#94a3b8',
              fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit',
            }}>
              <Icon name={s.icon} size={16} /> {s.label}
              <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: active ? (s.color || '#6366f1') : '#e2e8f0', color: active ? '#fff' : '#94a3b8' }}>{count}</span>
            </button>
          );
        })}
      </div>}

      {/* ADMIN: hudud bo'yicha jadval */}
      {isAdmin && (stats.tuman_breakdown || stats.mfy_breakdown) ? (
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <div style={{ padding: '16px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18 }}>🏢</span>
            <span style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>
              {user?.role === 'viloyat_admin' ? 'Tumanlar' : 'MFYlar'} bo'yicha muammolar hisoboti
            </span>
            <span style={{ marginLeft: 'auto', fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>
              {(stats.tuman_breakdown || stats.mfy_breakdown || []).length} ta {user?.role === 'viloyat_admin' ? 'tuman' : 'MFY'}
            </span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 850 }}>
              <thead>
                <tr style={{ background: '#fafbff' }}>
                  {['#', user?.role === 'viloyat_admin' ? 'Tuman' : 'MFY', 'Jami', 'Yangi', 'Jarayonda', 'Hal qilindi', 'Yopildi', 'Shoshilinch', "Muddati o'tgan", 'Javob berilgan'].map(h => (
                    <th key={h} style={{ padding: '12px 10px', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.4px', borderBottom: '2px solid #e2e8f0', textAlign: h === '#' || h === 'Tuman' || h === 'MFY' ? 'left' : 'center', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(stats.tuman_breakdown || stats.mfy_breakdown || []).map((row, i) => {
                  const name = row[user?.role === 'viloyat_admin' ? 'tuman' : 'mfy'] || "Noma'lum";
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#fafbff'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}>
                      <td style={{ padding: '11px 10px', fontSize: 12, color: '#94a3b8' }}>{i + 1}</td>
                      <td style={{ padding: '11px 10px', fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{name}</td>
                      <td style={{ padding: '11px 10px', textAlign: 'center', fontSize: 14, fontWeight: 800, color: '#1e40af' }}>{row.jami}</td>
                      <td style={{ textAlign: 'center' }}>{row.yangi_c > 0 ? <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: '#eef2ff', color: '#6366f1' }}>{row.yangi_c}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                      <td style={{ textAlign: 'center' }}>{row.jarayonda_c > 0 ? <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: '#fffbeb', color: '#ca8a04' }}>{row.jarayonda_c}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                      <td style={{ textAlign: 'center' }}>{row.hal_qilindi_c > 0 ? <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: '#f0fdf4', color: '#16a34a' }}>{row.hal_qilindi_c}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                      <td style={{ textAlign: 'center' }}>{row.yopildi_c > 0 ? <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: '#f8fafc', color: '#64748b' }}>{row.yopildi_c}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                      <td style={{ textAlign: 'center' }}>{row.shoshilinch_c > 0 ? <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 700, background: '#fef2f2', color: '#dc2626' }}>{row.shoshilinch_c}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                      <td style={{ textAlign: 'center' }}>{row.muddati_otgan > 0 ? <span style={{ color: '#ef4444', fontWeight: 700, fontSize: 13 }}>{row.muddati_otgan}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                      <td style={{ textAlign: 'center' }}>{row.javob_berilgan > 0 ? <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: '#ecfdf5', color: '#059669' }}>{row.javob_berilgan}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: '#f8fafc', borderTop: '2px solid #e2e8f0' }}>
                  <td style={{ padding: '12px 10px' }} />
                  <td style={{ padding: '12px 10px', fontSize: 13, fontWeight: 700, color: '#1e293b' }}>JAMI</td>
                  {(() => {
                    const bd = stats.tuman_breakdown || stats.mfy_breakdown || [];
                    const sum = (k) => bd.reduce((s, r) => s + (r[k] || 0), 0);
                    return (
                      <>
                        <td style={{ textAlign: 'center', fontSize: 14, fontWeight: 800, color: '#1e40af' }}>{sum('jami')}</td>
                        <td style={{ textAlign: 'center', fontWeight: 700, color: '#6366f1' }}>{sum('yangi_c')}</td>
                        <td style={{ textAlign: 'center', fontWeight: 700, color: '#ca8a04' }}>{sum('jarayonda_c')}</td>
                        <td style={{ textAlign: 'center', fontWeight: 700, color: '#16a34a' }}>{sum('hal_qilindi_c')}</td>
                        <td style={{ textAlign: 'center', fontWeight: 700, color: '#64748b' }}>{sum('yopildi_c')}</td>
                        <td style={{ textAlign: 'center', fontWeight: 700, color: '#dc2626' }}>{sum('shoshilinch_c')}</td>
                        <td style={{ textAlign: 'center', fontWeight: 700, color: '#ef4444' }}>{sum('muddati_otgan')}</td>
                        <td style={{ textAlign: 'center', fontWeight: 700, color: '#059669' }}>{sum('javob_berilgan')}</td>
                      </>
                    );
                  })()}
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      ) : (
        <>
          {/* MFY: filters + cards */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', flex: 1, minWidth: 180, maxWidth: 320 }}>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Qidirish..."
                style={{ width: '100%', padding: '11px 14px 11px 38px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#fff', fontSize: 13, fontFamily: 'inherit' }} />
              <div style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}><Icon name="search" size={15} /></div>
            </div>
            <select value={categoryF} onChange={e => setCategoryF(e.target.value)}
              style={{ padding: '11px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#fff', fontSize: 13, fontFamily: 'inherit' }}>
              <option value="">Barcha toifa</option>
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <select value={priorityF} onChange={e => setPriorityF(e.target.value)}
              style={{ padding: '11px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#fff', fontSize: 13, fontFamily: 'inherit' }}>
              <option value="">Barcha muhimlik</option>
              {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            <div style={{ marginLeft: 'auto' }}>
              <Button onClick={() => { setEditItem(null); setFormOpen(true); }} icon="plus">Muammo qo'shish</Button>
            </div>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
              <div style={{ display: 'inline-block', width: 28, height: 28, border: '3px solid #e2e8f0', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin .8s linear infinite' }} />
              <div style={{ marginTop: 10, fontSize: 13 }}>Yuklanmoqda...</div>
            </div>
          ) : problems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0' }}>
              <Icon name="clipboardList" size={48} color="#cbd5e1" />
              <p style={{ fontSize: 15, color: '#94a3b8', marginTop: 12 }}>Muammolar topilmadi</p>
            </div>
          ) : (
            <div className="prob-cards" style={{ display: 'grid', gap: 14 }}>
              {problems.map(p => (
                <ProblemCard key={p.id} problem={p} isAdmin={false}
                  onView={() => loadAndView(p.id)}
                  onEdit={() => loadAndEdit(p.id)}
                  onDelete={() => setDeleteId(p.id)}
                  onRespond={() => loadAndView(p.id).then(() => {})}
                  onStatusChange={handleStatusChange}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* CREATE/EDIT MODAL (faqat MFY) */}
      {!isAdmin && (
        <Modal open={formOpen || !!editItem} onClose={() => { setFormOpen(false); setEditItem(null); }}
          title={editItem ? "Muammoni tahrirlash" : "Yangi muammo"} width={620}>
          <ProblemForm problem={editItem} onSave={editItem ? handleUpdate : handleCreate}
            onClose={() => { setFormOpen(false); setEditItem(null); }} />
        </Modal>
      )}

      {/* VIEW MODAL */}
      <Modal open={!!viewItem} onClose={() => setViewItem(null)} title="Muammo tafsilotlari" width={660}>
        {viewItem && (
          <ProblemDetail
            problem={viewItem}
            isAdmin={isAdmin}
            onEdit={() => { setViewItem(null); setEditItem(viewItem); }}
            onStatusChange={(st) => { handleStatusChange(viewItem.id, st); setViewItem(null); }}
            onRespond={() => { setRespondItem(viewItem); }}
          />
        )}
      </Modal>

      {/* RESPOND MODAL (faqat admin) */}
      <Modal open={!!respondItem} onClose={() => setRespondItem(null)} title="Hokimlik javobi" width={560}>
        {respondItem && (
          <RespondForm problem={respondItem} onSave={handleRespond} onClose={() => setRespondItem(null)} />
        )}
      </Modal>

      {/* DELETE MODAL (faqat MFY) */}
      <Modal open={!!deleteId} onClose={() => setDeleteId(null)} title="O'chirishni tasdiqlang">
        <p style={{ fontSize: 14, color: '#64748b', marginBottom: 16 }}>Ushbu muammoni o'chirishni xohlaysizmi?</p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setDeleteId(null)}>Bekor qilish</Button>
          <Button variant="danger" onClick={handleDelete}>O'chirish</Button>
        </div>
      </Modal>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .prob-stats { grid-template-columns: repeat(6, 1fr); }
        .prob-cards { grid-template-columns: 1fr 1fr; }
        @media (max-width: 1100px) { .prob-stats { grid-template-columns: repeat(3, 1fr); } .prob-cards { grid-template-columns: 1fr; } }
        @media (max-width: 768px) { .prob-stats { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 480px) { .prob-stats { grid-template-columns: 1fr; } }
      `}</style>
    </div>
  );
}

// ===== STAT CARD =====
function StatCard({ icon, label, value, color }) {
  return (
    <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
      <div style={{ width: 42, height: 42, borderRadius: 12, background: color + '14', display: 'flex', alignItems: 'center', justifyContent: 'center', color, flexShrink: 0 }}>
        <Icon name={icon} size={20} />
      </div>
      <div>
        <div style={{ fontSize: 22, fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500, marginTop: 2 }}>{label}</div>
      </div>
    </div>
  );
}

// ===== PROBLEM CARD =====
function ProblemCard({ problem: p, isAdmin, onView, onEdit, onDelete, onStatusChange }) {
  const cat = getCat(p.category);
  const pri = getPri(p.priority);
  const st = getSt(p.status);
  const overdue = p.days_left !== null && p.days_left < 0 && !['hal_qilindi', 'yopildi'].includes(p.status);
  const urgent = p.priority === 'shoshilinch' && !['hal_qilindi', 'yopildi'].includes(p.status);
  const hasResponse = !!p.response;

  return (
    <div onClick={onView} style={{
      background: '#fff', borderRadius: 16,
      border: `1.5px solid ${overdue ? '#fecaca' : urgent ? '#fed7aa' : '#e2e8f0'}`,
      padding: '18px 20px', cursor: 'pointer', transition: 'all .2s',
      boxShadow: overdue ? '0 0 0 3px #fef2f2' : urgent ? '0 0 0 3px #fff7ed' : 'none',
    }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 30px rgba(99,102,241,0.08)'; e.currentTarget.style.borderColor = '#6366f1'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = overdue ? '0 0 0 3px #fef2f2' : urgent ? '0 0 0 3px #fff7ed' : 'none'; e.currentTarget.style.borderColor = overdue ? '#fecaca' : urgent ? '#fed7aa' : '#e2e8f0'; }}>

      {/* Badges */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: cat.bg, color: cat.color }}>
          <Icon name={cat.icon} size={12} /> {cat.label}
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: pri.bg, color: pri.color }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: pri.dot }} /> {pri.label}
        </span>
        {hasResponse && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: '#ecfdf5', color: '#059669' }}>
            <Icon name="checkCircle" size={12} /> Javob berilgan
          </span>
        )}
        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: st.bg || '#f8fafc', color: st.color || '#64748b' }}>
          <Icon name={st.icon} size={12} /> {st.label}
        </span>
      </div>

      {/* Title */}
      <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', marginBottom: 6, lineHeight: 1.4 }}>{p.title}</div>

      {/* Youth + location */}
      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <Icon name="users" size={13} /> {p.youth_name}
        {p.tuman && <span style={{ fontSize: 11, color: '#94a3b8' }}>• {p.tuman}</span>}
        {p.mfy && <span style={{ fontSize: 11, color: '#94a3b8' }}>• {p.mfy}</span>}
      </div>

      {/* Response preview for admin */}
      {hasResponse && (
        <div style={{ padding: '8px 12px', borderRadius: 10, background: '#f0fdf4', border: '1px solid #bbf7d0', marginBottom: 10, fontSize: 12, color: '#166534', lineHeight: 1.5 }}>
          <strong>Javob:</strong> {p.response.length > 100 ? p.response.slice(0, 100) + '...' : p.response}
        </div>
      )}

      {/* Bottom: Deadline + Actions */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 10, borderTop: '1px solid #f1f5f9' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {p.deadline && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: overdue ? '#ef4444' : p.days_left <= 3 ? '#f59e0b' : '#94a3b8' }}>
              <Icon name="calendar" size={13} />
              {p.deadline}
              {p.days_left !== null && !['hal_qilindi', 'yopildi'].includes(p.status) && (
                <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 6, background: overdue ? '#fef2f2' : p.days_left <= 3 ? '#fffbeb' : '#f8fafc', marginLeft: 2 }}>
                  {overdue ? `${Math.abs(p.days_left)} kun o'tdi` : `${p.days_left} kun qoldi`}
                </span>
              )}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
          {isAdmin ? (
            /* Admin: faqat javob berish tugmasi */
            !hasResponse && !['hal_qilindi', 'yopildi'].includes(p.status) && (
              <button onClick={onView} style={{ fontSize: 11, fontWeight: 600, padding: '6px 14px', borderRadius: 8, border: 'none', background: '#ede9fe', color: '#6366f1', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontFamily: 'inherit' }}>
                <Icon name="edit" size={12} /> Javob berish
              </button>
            )
          ) : (
            /* MFY: tahrirlash, o'chirish */
            <>
              <button onClick={() => onEdit()} style={{ width: 30, height: 30, borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#94a3b8' }}>
                <Icon name="edit" size={13} />
              </button>
              <button onClick={() => onDelete()} style={{ width: 30, height: 30, borderRadius: 8, border: '1px solid #fecaca', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#ef4444' }}>
                <Icon name="trash" size={13} />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ===== RESPOND FORM (Hokimlik javobi) =====
function RespondForm({ problem, onSave, onClose }) {
  const [response, setResponse] = useState(problem.response || '');
  const [status, setStatus] = useState('');

  return (
    <div>
      {/* Muammo haqida qisqa */}
      <div style={{ padding: '14px 16px', borderRadius: 12, background: '#f8fafc', border: '1px solid #e2e8f0', marginBottom: 18 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>{problem.title}</div>
        <div style={{ fontSize: 12, color: '#64748b', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <span><Icon name="users" size={12} /> {problem.youth_name}</span>
          {problem.tuman && <span>• {problem.tuman}</span>}
          {problem.mfy && <span>• {problem.mfy}</span>}
        </div>
        {problem.description && (
          <p style={{ fontSize: 13, color: '#64748b', marginTop: 8, lineHeight: 1.5 }}>{problem.description}</p>
        )}
      </div>

      {/* Javob maydoni */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', display: 'block', marginBottom: 6 }}>
          Hokimlik javobi <span style={{ color: '#ef4444' }}>*</span>
        </label>
        <textarea value={response} onChange={e => setResponse(e.target.value)} rows={5}
          placeholder="Muammoga javob yozing... (ko'rilgan chora, topshiriq, taklif)"
          style={{
            width: '100%', padding: '14px 16px', borderRadius: 12, border: '2px solid #e2e8f0',
            background: '#fff', fontSize: 14, fontFamily: 'inherit', resize: 'vertical',
            transition: 'border .2s', outline: 'none', boxSizing: 'border-box',
          }}
          onFocus={e => e.target.style.borderColor = '#6366f1'}
          onBlur={e => e.target.style.borderColor = '#e2e8f0'}
        />
      </div>

      {/* Holatni o'zgartirish */}
      <div style={{ marginBottom: 20 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', display: 'block', marginBottom: 8 }}>Holatni o'zgartirish (ixtiyoriy)</label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[
            { value: '', label: "O'zgartirmaslik", color: '#64748b', bg: '#f8fafc' },
            { value: 'jarayonda', label: 'Jarayonda', color: '#f59e0b', bg: '#fffbeb' },
            { value: 'hal_qilindi', label: 'Hal qilindi', color: '#22c55e', bg: '#f0fdf4' },
          ].map(s => (
            <button key={s.value} onClick={() => setStatus(s.value)} style={{
              padding: '8px 16px', borderRadius: 10,
              border: `2px solid ${status === s.value ? s.color : '#e2e8f0'}`,
              background: status === s.value ? s.bg : '#fff',
              color: status === s.value ? s.color : '#94a3b8',
              fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            }}>{s.label}</button>
          ))}
        </div>
      </div>

      {/* Tugmalar */}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 14, borderTop: '1px solid #f1f5f9' }}>
        <Button variant="ghost" onClick={onClose}>Bekor qilish</Button>
        <Button onClick={() => onSave({ response, status })} disabled={!response.trim()} icon="checkCircle">
          Javob yuborish
        </Button>
      </div>
    </div>
  );
}

// ===== PROBLEM FORM (MFY uchun) =====
function ProblemForm({ problem, onSave, onClose }) {
  const [youthList, setYouthList] = useState([]);
  const [form, setForm] = useState({
    youth: problem?.youth || '',
    category: problem?.category || 'ishsizlik',
    title: problem?.title || '',
    description: problem?.description || '',
    priority: problem?.priority || "o'rta",
    status: problem?.status || 'yangi',
    responsible: problem?.responsible || '',
    deadline: problem?.deadline || '',
    solution: problem?.solution || '',
  });

  useEffect(() => {
    youthAPI.mini().then(r => setYouthList(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 13, fontWeight: 500, color: '#64748b', display: 'block', marginBottom: 6 }}>Yosh <span style={{ color: '#ef4444' }}>*</span></label>
        <select value={form.youth} onChange={e => set('youth', e.target.value)}
          style={{ width: '100%', padding: '13px 16px', borderRadius: 12, border: '2px solid #e2e8f0', fontSize: 14, fontFamily: 'inherit' }}>
          <option value="">Yoshni tanlang...</option>
          {youthList.map(y => <option key={y.id} value={y.id}>{y.full_name} ({y.age} yosh)</option>)}
        </select>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 500, color: '#64748b', display: 'block', marginBottom: 6 }}>Toifa</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {CATEGORIES.map(c => (
              <button key={c.value} onClick={() => set('category', c.value)} style={{
                display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 20,
                border: form.category === c.value ? `2px solid ${c.color}` : '2px solid #e2e8f0',
                background: form.category === c.value ? c.bg : '#fff',
                color: form.category === c.value ? c.color : '#94a3b8',
                fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              }}><Icon name={c.icon} size={12} /> {c.label}</button>
            ))}
          </div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 500, color: '#64748b', display: 'block', marginBottom: 6 }}>Muhimlik</label>
          <div style={{ display: 'flex', gap: 6 }}>
            {PRIORITIES.map(p => (
              <button key={p.value} onClick={() => set('priority', p.value)} style={{
                display: 'flex', alignItems: 'center', gap: 5, padding: '8px 14px', borderRadius: 10, flex: 1,
                border: form.priority === p.value ? `2px solid ${p.color}` : '2px solid #e2e8f0',
                background: form.priority === p.value ? p.bg : '#fff',
                color: form.priority === p.value ? p.color : '#94a3b8',
                fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', justifyContent: 'center',
              }}><span style={{ width: 7, height: 7, borderRadius: '50%', background: p.dot }} /> {p.label}</button>
            ))}
          </div>
        </div>
      </div>

      <Input label="Muammo sarlavhasi" value={form.title} onChange={v => set('title', v)} required placeholder="Qisqa tavsif..." />

      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 13, fontWeight: 500, color: '#64748b', display: 'block', marginBottom: 6 }}>Batafsil tavsif</label>
        <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={3} placeholder="Muammo haqida batafsil..."
          style={{ width: '100%', padding: '13px 16px', borderRadius: 12, border: '2px solid #e2e8f0', fontSize: 14, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Input label="Mas'ul shaxs" value={form.responsible} onChange={v => set('responsible', v)} placeholder="FIO" />
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 500, color: '#64748b', display: 'block', marginBottom: 6 }}>Muddat</label>
          <input type="date" value={form.deadline} onChange={e => set('deadline', e.target.value)}
            style={{ width: '100%', padding: '13px 16px', borderRadius: 12, border: '2px solid #e2e8f0', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }} />
        </div>
      </div>

      {problem && <Select label="Holat" value={form.status} onChange={v => set('status', v)} options={STATUSES.filter(s => s.value).map(s => ({ value: s.value, label: s.label }))} />}

      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 13, fontWeight: 500, color: '#64748b', display: 'block', marginBottom: 6 }}>Yechim / Ko'rilgan chora</label>
        <textarea value={form.solution} onChange={e => set('solution', e.target.value)} rows={2} placeholder="Qanday chora ko'rildi..."
          style={{ width: '100%', padding: '13px 16px', borderRadius: 12, border: '2px solid #e2e8f0', fontSize: 14, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
      </div>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
        <Button variant="ghost" onClick={onClose}>Bekor qilish</Button>
        <Button onClick={() => { if (form.youth && form.title) onSave(form); }} disabled={!form.youth || !form.title}>{problem ? 'Saqlash' : "Qo'shish"}</Button>
      </div>
    </div>
  );
}

// ===== PROBLEM DETAIL =====
function ProblemDetail({ problem: p, isAdmin, onEdit, onStatusChange, onRespond }) {
  const cat = getCat(p.category);
  const pri = getPri(p.priority);
  const st = getSt(p.status);
  const overdue = p.days_left !== null && p.days_left < 0 && !['hal_qilindi', 'yopildi'].includes(p.status);

  const Row = ({ label, value, icon }) => value ? (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
      <div style={{ width: 32, height: 32, borderRadius: 8, background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', flexShrink: 0 }}>
        <Icon name={icon} size={15} />
      </div>
      <div>
        <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>{label}</div>
        <div style={{ fontSize: 14, color: '#1e293b', fontWeight: 500, marginTop: 2 }}>{value}</div>
      </div>
    </div>
  ) : null;

  return (
    <div>
      {/* Badges */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, padding: '5px 14px', borderRadius: 20, background: cat.bg, color: cat.color }}>
          <Icon name={cat.icon} size={14} /> {cat.label}
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, padding: '5px 14px', borderRadius: 20, background: pri.bg, color: pri.color }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: pri.dot }} /> {pri.label}
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, padding: '5px 14px', borderRadius: 20, background: st.bg, color: st.color }}>
          <Icon name={st.icon} size={14} /> {st.label}
        </span>
        {overdue && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, padding: '5px 14px', borderRadius: 20, background: '#fef2f2', color: '#ef4444' }}>
            <Icon name="alertTriangle" size={14} /> Muddati o'tgan!
          </span>
        )}
      </div>

      <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>{p.title}</h3>

      {p.description && (
        <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.6, marginBottom: 16, padding: '12px 14px', background: '#f8fafc', borderRadius: 10 }}>{p.description}</p>
      )}

      <Row icon="users" label="Yosh" value={`${p.youth_name}${p.youth_phone ? ' • ' + p.youth_phone : ''}`} />
      {p.tuman && <Row icon="building" label="Tuman" value={p.tuman} />}
      {p.mfy && <Row icon="home" label="MFY" value={p.mfy} />}
      <Row icon="users" label="Mas'ul" value={p.responsible} />
      <Row icon="calendar" label="Muddat" value={p.deadline ? (
        <span>{p.deadline} {overdue ? <span style={{ color: '#ef4444', fontWeight: 600 }}> ({Math.abs(p.days_left)} kun o'tdi)</span> : p.days_left !== null && !['hal_qilindi', 'yopildi'].includes(p.status) ? <span style={{ color: p.days_left <= 3 ? '#f59e0b' : '#94a3b8' }}> ({p.days_left} kun qoldi)</span> : null}</span>
      ) : null} />
      {p.solution && <Row icon="checkCircle" label="Yechim / Ko'rilgan chora" value={p.solution} />}
      <Row icon="clock" label="Yaratilgan" value={p.created_at?.slice(0, 10)} />

      {/* ===== HOKIMLIK JAVOBI ===== */}
      {p.response && (
        <div style={{ marginTop: 16, padding: '16px 18px', borderRadius: 14, background: 'linear-gradient(135deg, #f0fdf4, #ecfdf5)', border: '1.5px solid #86efac' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="shield" size={16} color="#16a34a" />
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#166534' }}>Hokimlik javobi</div>
              <div style={{ fontSize: 11, color: '#4ade80' }}>
                {p.responded_by_name && `${p.responded_by_name} • `}{p.responded_at?.slice(0, 10)}
              </div>
            </div>
          </div>
          <p style={{ fontSize: 14, color: '#166534', lineHeight: 1.7, margin: 0 }}>{p.response}</p>
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20, paddingTop: 14, borderTop: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
        {isAdmin ? (
          /* Admin: faqat javob */
          <>
            {!p.response && !['hal_qilindi', 'yopildi'].includes(p.status) && (
              <Button onClick={onRespond} icon="edit">Javob berish</Button>
            )}
            {p.response && !['hal_qilindi', 'yopildi'].includes(p.status) && (
              <Button variant="secondary" size="sm" onClick={onRespond} icon="edit">Javobni tahrirlash</Button>
            )}
          </>
        ) : (
          /* MFY: tahrirlash + status */
          <>
            <Button variant="ghost" size="sm" onClick={onEdit} icon="edit">Tahrirlash</Button>
            {p.status === 'yangi' && <Button size="sm" onClick={() => onStatusChange('jarayonda')} icon="clock">Ishga tushirish</Button>}
            {p.status === 'jarayonda' && <Button size="sm" onClick={() => onStatusChange('hal_qilindi')} icon="checkCircle">Hal qilindi</Button>}
          </>
        )}
      </div>
    </div>
  );
}
